import axios, { AxiosError } from 'axios';
import qs from 'qs';
import { makeUseAxios } from 'axios-hooks';
import { createAuthRefresh } from 'axios-auth-refresh';

import store from '../store/storeConfig';
import { logout, refreshTokens } from '../store/oauth/reducer';
import { AccessRoles } from '../constants/enums';

/**
 * What nginx routes to the gateway. Exported because a link the *browser* follows has to carry it
 * too: axios prepends this to every request, so a path that works through `instance` 404s into the
 * SPA when it is put in an `<a href>` (signup's Download PDF, Charles 2026-09-16).
 */
export const API_BASE_PATH = '/api';

const instance = axios.create({
  baseURL: API_BASE_PATH,
  paramsSerializer: (params) => {
    return qs.stringify(params, {
      skipNulls: true
    });
  }
});

/**
 * The 7000's codes for "this session has not presented its second factor yet". A 401 carrying one
 * of these does not mean the session lapsed — it means the factor is still outstanding, which is
 * the normal state between signing in and entering the code.
 */
const TFA_OUTSTANDING_CODES = [81, 82, 83];

// avoid catching itself if refresh fails...
const shouldRefresh = (error?: AxiosError | null) => {
  if (error?.config?.url === '/clinician-gateway/auth/refresh' || error?.response?.status !== 401) {
    return false;
  }

  /*
   * A factor that has not been presented is not a token that has expired, and refreshing makes it
   * worse: the 7000 checks the factor per access token, so a new token has no factor either, the
   * next call 401s, and the loop feeds itself. Seen as a run of auth/refresh calls behind the TFA
   * screen on a first login, where the 7000 answers 81 to everything until setup is finished
   * (Alex, 2026-09-15).
   */
  const code = Number((error?.response?.data as { code?: number | string } | undefined)?.code);
  return !TFA_OUTSTANDING_CODES.includes(code);
};

const refreshAuthLogic = async () => {
  const { oauth } = store.getState();
  if (!oauth?.data.refresh_token) return Promise.reject();

  try {
    const response = await instance.post('/clinician-gateway/auth/refresh', {
      refresh_token: oauth?.data.refresh_token
    });

    if (response.data.accessToken) {
      store.dispatch(
        refreshTokens({
          access_token: response.data.accessToken,
          refresh_token: response.data.refreshToken
        })
      );
      return Promise.resolve();
    } else {
      store.dispatch(logout());
      return Promise.reject();
    }
  } catch (e) {
    store.dispatch(logout());
    return Promise.reject(e);
  }
};

createAuthRefresh(instance, refreshAuthLogic, { shouldRefresh, deduplicateRefresh: false });

instance.interceptors.request.use((config) => {
  const { oauth } = store.getState();
  if (oauth.data?.access_token && config.headers)
    config.headers['Authorization'] = 'Bearer ' + oauth.data?.access_token;

  // Rewrite clinician → sysop for sysop users (PHP backend only)
  if (parseInt(oauth.data?.role) === AccessRoles.SYSOP && config.url?.startsWith('/clinician/')) {
    config.url = config.url.replace('/clinician/', '/sysop/');
  }

  return config;
});

export const useAxios = makeUseAxios({
  axios: instance
});

export default instance;
